import React, { useCallback, useEffect, useState } from 'react';
import { Redirect, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { ProductType } from '@tink/core/src/types';
import { ProductDimensionPay } from '../../../models/product';
import { useVersion } from '../../../components/ApplicationProviders/VersionProvider/VersionProvider';
import { LoadingOverlay } from '../../screens/LoadingOverlay/LoadingOverlay';
import { AppState, ConnectedPropsWithoutDispatch } from '../../../stores/redux-store';
import { handoffSessionService } from '../../../services/handoff-session';
import { isConsumedHandoffSession } from '../../../models/handoffSession';
import { useAnalytics } from '../../Analytics/AnalyticsProvider';
import { setAppContext as setAppContextImport } from '../../../utilities/setAppContext';

export type HandoffExchangePresenterProps = PropsFromRedux & {
  setAppContext?: typeof setAppContextImport;
};

export const HandoffExchangePresenter: React.FC<HandoffExchangePresenterProps> = ({
  consumeUrlHandoffSession,
  setAppContext = setAppContextImport,
  handoffSession,
}) => {
  const {
    version: { version },
  } = useVersion();
  const { tinklinkAnalytics } = useAnalytics();
  const { handoffSessionId } = useParams<{ handoffSessionId: string }>();
  const [isAnalyticsInitialized, setAnalyticsInitialized] = useState(false);
  const initAnalytics = useCallback(() => {
    /**
     * at this point we have no idea from where we are coming
     * there is no information about productType nor productDimension
     * so in case of 200 response we redirect to some valid TL url and init analytics again
     * but when request error occurs we have to initialize with ANY
     */
    const { parameters } = setAppContext({
      version,
      productType: ProductType.ANY,
      productDimension: ProductDimensionPay.ANY,
      validateRedirectUrlParameters: false,
    });

    tinklinkAnalytics.initialize({
      appParameters: parameters,
      version,
      productType: ProductType.ANY,
    });
    setAnalyticsInitialized(true);
  }, [setAppContext, tinklinkAnalytics, version]);

  useEffect(() => {
    if (!handoffSession) {
      if (isAnalyticsInitialized) {
        consumeUrlHandoffSession(handoffSessionId);
      } else {
        initAnalytics();
      }
    }
  }, [
    handoffSession,
    consumeUrlHandoffSession,
    handoffSessionId,
    isAnalyticsInitialized,
    initAnalytics,
  ]);

  if (isConsumedHandoffSession(handoffSession) && handoffSession.url) {
    return <Redirect to={handoffSession.url} />;
  }

  return <LoadingOverlay />;
};

const mapStateToProps = (state: AppState) => ({
  handoffSession: state.handoffSession.data,
});

const mapDispatchToProps = {
  consumeUrlHandoffSession: handoffSessionService.consumeUrlHandoffSession,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedPropsWithoutDispatch<typeof connector>;

export const HandoffExchange = connector(HandoffExchangePresenter);
